import React, { useMemo } from "react";
import { useRouter } from "next/router";
import {
  Community,
  CommunityEvents,
  CommunityFeed,
  CommunityStories,
  CommunitySubCommunities,
  OrgBootstrap,
} from "@dreambigger/shared/src/types";
import { Spin, Head } from "@dreambigger/design-system";
import { communityPaths } from "@dreambigger/shared/src/api/communities";
import { organizationPaths } from "@dreambigger/shared/src/api/organizations";
import cotributeDefaults from "@dreambigger/shared/src/assets/cotribute-defaults";
import StoryBoard from "@dreambigger/shared/src/components/board/stories-board";
import useSWR from "swr";
import SubCommunityBoard from "./sub-community-board";
import EventsBoard from "./events-board";
import {
  boardFetcherFn,
  FilterDefaults,
} from "@dreambigger/shared/src/types/board";
import FeedBoard from "./feed-board";
import { generateWeserveQueryParams } from "@dreambigger/shared/src/utils/query-params";

type FilteredBoardType =
  | "events"
  | "stories"
  | "collections"
  | "subCommunities"
  | "feed";

type CommunityEntities =
  | CommunityEvents
  | CommunityStories
  | CommunityFeed
  | CommunitySubCommunities;

type BoardWrapperProps = {
  community: Community;
  initialData?: CommunityEntities[];
  orgBootstrap: OrgBootstrap;
  boardType: FilteredBoardType;
  filterDefaults?: FilterDefaults;
};

export interface BoardProps {
  community: Community;
  orgBootstrap: OrgBootstrap;
  filterDefaults?: FilterDefaults;
  initialData?: any;
}

// DEFINE BOARDS - Click into these boards to adjust how they render.
const boards = {
  events: EventsBoard,
  collections: null,
  subCommunities: SubCommunityBoard,
  feed: FeedBoard,
};

// ***************************************************
// * Component
// ***************************************************

const BoardWrapper = ({
  community: initialCommunityData,
  initialData: initialData,
  orgBootstrap: initialOrgBootstrap,
  boardType,
  filterDefaults,
}: BoardWrapperProps) => {
  const { isFallback } = useRouter();

  // GET DATA FOR BOARDS
  // ** Orgbootstrap
  const { data: orgBootstrapData } = useSWR<OrgBootstrap, any>(
    initialCommunityData
      ? organizationPaths.getOrgBootstrapPath(
          initialCommunityData.organization.id
        )
      : null,
    boardFetcherFn,
    {
      fallbackData: initialOrgBootstrap,
    }
  );
  // ** Community Data
  const { data: communityData } = useSWR<Community, any>(
    initialCommunityData
      ? communityPaths.getBySlug(initialCommunityData.slug)
      : null,
    boardFetcherFn,
    {
      fallbackData: initialCommunityData,
    }
  );
  const community = communityData || initialCommunityData;
  const orgBootstrap = orgBootstrapData || initialOrgBootstrap;

  // STORE CORRECT BOARD IN BOARD VARIABLE
  // * To adjust what is rendered for each board, look at "boards" variable above.
  const board = useMemo(() => {
    switch (boardType) {
      case "collections":
      case "events":
      case "feed":
      case "subCommunities":
        const BoardComponent = boards[boardType]!;
        return (
          <BoardComponent
            community={community}
            orgBootstrap={orgBootstrap}
            initialData={initialData as any}
            filterDefaults={filterDefaults as any}
          />
        );
      case "stories":
        const paginationLength =
          community?.meta?.feeds?.paginationLength ||
          filterDefaults?.pagination ||
          3;
        const getUrlKey = (pageIndex: number) =>
          organizationPaths.getStories(
            community.organization.id,
            generateWeserveQueryParams({
              limit: String(paginationLength),
              sort: filterDefaults!.sort,
              direction: filterDefaults!.direction,
              offset: String(paginationLength * pageIndex),
            })
          );
        return (
          <StoryBoard
            boardTitle={`${
              community.organization?.name &&
              community.organization?.name.toUpperCase() + " "
            }STORIES`}
            getUrlKey={getUrlKey}
            showStoryComments={
              community?.organization?.meta?.showInspireComments || false
            }
            community={community}
          />
        );
    }
  }, [boardType, boards, community, filterDefaults, orgBootstrap]);

  const tabIcon =
    community?.meta?.brand?.tabIcon || cotributeDefaults.tabIconIco;

  // DISPLAY SPINNER WHILE LOADING
  if (isFallback || !community) {
    return <Spin className="w-100" style={{ marginTop: 150 }} />;
  }

  // GUARD CLAUSE
  if (!board) {
    console.info(`no component yet build for ${boardType}`);
    return null;
  }

  // CACHE COMMUNITY
  if ((process as any).browser && communityData) {
    localStorage.setItem("cache-community", JSON.stringify(communityData));
  }

  // ***************************************************
  // * Render
  // ***************************************************

  return (
    <>
      <Head
        favicon={tabIcon}
        title={community?.meta?.stats?.title}
        description={community?.meta?.seo?.description}
        siteName={community?.meta?.seo?.site_name}
        image={community?.meta?.seo?.image}
      />
      <div className="flex flex-column justify-center default-wrapper-padding pv-6">
        {board}
      </div>
    </>
  );
};

export default BoardWrapper;
